/**
 * Pure preference filters applied to the curriculum item set before it
 * reaches the session scheduler.
 */

import type { Item, SessionConfig } from "@music-app/learning-system";

export type SessionMix = "review-only" | "balanced" | "new-focus";

const MIX_PRESETS: Record<SessionMix, SessionConfig["mix"]> = {
  "review-only": { review: 1.0, new: 0 },
  "balanced":    { review: 0.5, new: 0.5 },
  "new-focus":   { review: 0.2, new: 0.8 },
};

export function buildSessionConfig(
  targetMinutes: number,
  mix: SessionMix,
): SessionConfig {
  return { targetMinutes, mix: MIX_PRESETS[mix], itemsPerMinute: 6 };
}

export interface PrefsFilter {
  /** When false, drop the major leading tone (degree 7) from scale-degree pools. */
  includeLeadingTone: boolean;
}

/**
 * Drop the major leading tone (degree 7) from scale-degree items when the
 * user has the preference disabled. Other items pass through untouched.
 * In minor (do-based: `te`; la-based: `sol`), degree 7 is the subtonic
 * not the leading tone, so the filter is scoped to mode === "major".
 */
export function filterByPrefs(items: Item[], prefs: PrefsFilter): Item[] {
  if (prefs.includeLeadingTone) return items;
  return items.filter(
    (it) =>
      !(
        it.exerciseType === "scale-degree-recognition" &&
        it.params.mode === "major" &&
        it.params.degree === 7
      ),
  );
}
